import { useEffect, useState } from "react";
import { Button, Modal, ModalBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createAdminCompanyDevice, updateAdminCompanyDevice } from "../../slices/admin/thunk";
import { getContractEndDate } from "../../helpers/data_conversion_helper";

const AdminDeviceModal = ({ show, onCloseClick, data, selectedCompany }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isShowError, setShowError] = useState(false);
    const companies = useSelector(state => state.Admin.company);
    const isNewDevice = data === null;

    const defaultDeviceData = {
        id: '',
        monitor_location_device_id: null,
        admin_company_id: '',
        toilet_height: '',
        is_installed_thermal_sensor: false,
        is_installed_ceiling_light: false,
        is_installed_signal_tower: false,
        is_using_opuschat: false,
        opuschat_url: '',
        installation_date: null,
        uninstallation_date: null,
        ai_version: '',
        remark: '',
        created_at: Math.floor(Date.now() / 1000),
        updated_at: Math.floor(Date.now() / 1000),
        is_effective: true,
    };

    const [deviceData, setDeviceData] = useState(() => ({
        ...defaultDeviceData,
        ...(data ?? {})
    }));

    useEffect(() => {
        if (show) {
            if (isNewDevice) {
                setDeviceData({
                    ...defaultDeviceData,
                    admin_company_id: companies.length > 0 ? companies[0].id : "",
                });
            } else {
                setDeviceData({
                    ...defaultDeviceData,
                    ...(data ?? {})
                });
            }
        }
    }, [show, isNewDevice, companies, data]);



    const closeModal = (saved) => {
        setShowError(false);
        onCloseClick(saved ? deviceData : null);
    };

    // Safe input handler with fallbacks
    const handleChange = (field) => (e) => {
        let value = e.target.value || '';
        if (
            field === 'is_installed_thermal_sensor' ||
            field === 'is_installed_ceiling_light' ||
            field === 'is_installed_signal_tower' ||
            field === 'is_using_opuschat' ||
            field === 'is_effective'
        ) {
            value = e.target.value === 'true' || e.target.checked;
        }

        setDeviceData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Modal isOpen={show} toggle={() => { closeModal(false) }} centered={true} size="lg">
            <div className="modal-header">
                <div className="modal-title">{isNewDevice ? t('PageAdmin.Btn.CreateDevice') : t('PageAdmin.Btn.ModifyDevice')}</div>
                <div className="d-flex flex-row modal-close-btn">
                    <Button type="button" className="btn-close btn-sm btn-light" onClick={() => { closeModal(false); }} ></Button>
                </div>
            </div>
            <ModalBody className="d-flex flex-column">
                <form>
                    {!isNewDevice &&
                        <div className="row">
                            <div className="col-md-3">
                                <label className="col-form-label" htmlFor="admin-device-id">{t('PageAdmin.CompanyDeviceTable.ID')}</label>
                            </div>
                            <div className="col-md-9">
                                <input type="text" className="form-control" id="admin-device-id" value={deviceData.id} disabled />
                            </div>
                        </div>
                    }
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="admin-device-company">{t('PageAdmin.CompanyTable.Name')}</label>
                        </div>
                        <div className="col-md-9">
                            <select
                                className="form-control"
                                id="admin-device-company"
                                value={deviceData.admin_company_id}
                                onChange={handleChange("admin_company_id")}
                                disabled={!isNewDevice}
                            >
                                {
                                    companies.map(company => (
                                        <option key={company.id} value={company.id}>{company.name}</option>
                                    ))
                                }
                            </select>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="monitor-location-device-id">{t('PageAdmin.CompanyDeviceTable.SystemDeviceID')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="text" className="form-control" id="monitor-location-device-id" value={deviceData.monitor_location_device_id} onChange={handleChange("monitor_location_device_id")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="admin-device-status">{t('PageAdmin.CompanyDeviceTable.Status')}</label>
                        </div>
                        <div className="col-md-9">
                            <select
                                className="form-control"
                                id="admin-device-status"
                                value={deviceData.is_effective}
                                onChange={handleChange("is_effective")}
                            >
                                <option value={true}>{t('PageAdmin.DeviceStatus.Normal')}</option>
                                <option value={false}>{t('PageAdmin.DeviceStatus.Stopped')}</option>
                            </select>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="toilet-height">{t('PageAdmin.CompanyDeviceTable.ToiletHeight')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="text" className="form-control" id="toilet-height" value={deviceData.toilet_height} onChange={handleChange("toilet_height")} />
                        </div>
                    </div>
                    <div className="row align-items-center">
                        <div className="col-md-3">
                            <label className="col-form-label">{t('PageAdmin.CompanyDeviceTable.IsInstalledThermalSensor')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="checkbox" className="form-check-input" checked={!!deviceData.is_installed_thermal_sensor} onChange={handleChange("is_installed_thermal_sensor")} />
                        </div>
                    </div>
                    <div className="row align-items-center">
                        <div className="col-md-3">
                            <label className="col-form-label">{t('PageAdmin.CompanyDeviceTable.IsInstalledCeilingLight')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="checkbox" className="form-check-input" checked={!!deviceData.is_installed_ceiling_light} onChange={handleChange("is_installed_ceiling_light")} />
                        </div>
                    </div>
                    <div className="row align-items-center">
                        <div className="col-md-3">
                            <label className="col-form-label">{t('PageAdmin.CompanyDeviceTable.IsInstalledSignalTower')}</label>
                        </div>
                        <div className="col-md-9">
                            <input className="form-check-input" type="checkbox" checked={!!deviceData.is_installed_signal_tower} onChange={handleChange("is_installed_signal_tower")} />
                        </div>
                    </div>
                    <div className="row align-items-center">
                        <div className="col-md-3">
                            <label className="col-form-label">{t('PageAdmin.CompanyDeviceTable.IsUsingOpuschat')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="checkbox" className="form-check-input" checked={!!deviceData.is_using_opuschat} onChange={handleChange("is_using_opuschat")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="opuschat-url">{t('PageAdmin.CompanyDeviceTable.OpuschatLink')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="text" className="form-control" id="opuschat-url" value={deviceData.opuschat_url} onChange={handleChange("opuschat_url")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="installation-date">{t('PageAdmin.CompanyDeviceTable.InstalledDate')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="date" className="form-control" id="installation-date" value={deviceData.installation_date} onChange={handleChange("installation_date")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="uninstallation-date">{t('PageAdmin.CompanyDeviceTable.UninstalledDate')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="date" className="form-control" id="uninstallation-date" value={deviceData.uninstallation_date} onChange={handleChange("uninstallation_date")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="uninstallation-date">{t('PageAdmin.CompanyDeviceTable.ContractEndDate')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="text" className="form-control" id="uninstallation-date" value={getContractEndDate(deviceData)} disabled={true} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="ai-version">{t('PageAdmin.CompanyDeviceTable.AIVersion')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="text" className="form-control" id="ai-version" value={deviceData.ai_version} onChange={handleChange("ai_version")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="remark">{t('PageAdmin.CompanyDeviceTable.Remark')}</label>
                        </div>
                        <div className="col-md-9">
                            <textarea
                                rows={3}
                                className="form-control"
                                id="remark"
                                value={deviceData.remark || ''}
                                onChange={handleChange("remark")}
                                style={{ resize: 'vertical', overflowY: 'auto' }}
                            />
                        </div>
                    </div>
                </form>
                <div className="d-flex flex-column align-items-center company-save-modal-btn-container">
                    {isShowError ? <div className="company-save-modal-error">{t('PageAdmin.SaveDeviceModal.SaveError')}</div> : null}
                    <div className="d-flex flex-row justify-content-center ">
                        <Button className="btn-sm report-save-modal-btn" color="primary" onClick={() => {
                            closeModal(false)
                        }}> {t('PageReport.SaveReportModal.BtnCancel')} </Button>
                        <Button className="btn-sm report-save-modal-btn" color="primary" onClick={async () => {
                            let result = false;
                            if (isNewDevice) {
                                result = await dispatch(createAdminCompanyDevice(deviceData, selectedCompany));
                            } else {
                                result = await dispatch(updateAdminCompanyDevice(deviceData));
                            }
                            if (result === true) {
                                setShowError(false);
                                closeModal(true);
                            } else {
                                setShowError(true);
                            }
                        }}> {t('PageReport.SaveReportModal.BtnSave')} </Button>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
}

export default AdminDeviceModal;